import { CIAppliedFilter, CIAppliedListFilter, Loader, Modal, NoDataAvailable, UnableToFetch, useToastMessage, usePaginationPreference } from 'ccs-ui-components';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { IServiceScheduleMaintenance, ScheduledMaintenanceRqst } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { cleardeleteScheduledMaintenance, deleteScheduledMaintenanceRequest } from '../../../../store/actions/sagaActions/schedule-maintenance/DeleteScheduledMaintenanceSagaActions';
import { getScheduledMaintenanceRequest } from '../../../../store/actions/sagaActions/schedule-maintenance/GetScheduledMaintenanceSagaActions';
import { cleargetMaintenanceStatus, getMaintenanceStatusRequest } from '../../../../store/actions/sagaActions/schedule-maintenance/PostMaintenanceStatusSagaActions';
import { addFilterMaintenanceData, addSortMaintenanceData } from '../../../../store/reducer/schedule-maintenance/MaintenanceFilterReducer';
import { clearSmLogHistory } from '../../../../store/reducer/schedule-maintenance/SmLogHistoryReducer';
import { getFilterParams } from '../../../../utilities/schedule-maintenance/MaintenanceListUtils';
import { SCHEDULE_MAINTENANCE_ROUTE } from '../../schedule-maintenance-wrapper/scheduled-maintenance-details-wrapper/utilities/AddScheduleMaintenanceUtils';
import classes from '../MaintenanceList.module.scss';
import { HeadFilters, InnerDrop } from '../update-sm-status/UpdateSmStatus';
import MaintenanceTableList from './MaintenanceListHelper';
import { clearSaveMaintenance } from '../../../../store/reducer/schedule-maintenance/SaveMaintenanceReducer';

export const MaintenanceList = () => {
  const { t: translate } = useTranslation();
  const route = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const deleteDispatch = useDispatch<AppDispatch>();
  const { getPreferredPageSize, persistPageSize } = usePaginationPreference();

  const { searchText: reduxSearch, status: reduxStatus, sortColumn: reduxSortColumn, sortType: reduxSortType } = useSelector(
    (state) => state.filterMaintenanceReducer
  );

  const params = useMemo(
    () => getFilterParams(route.search, { searchText: reduxSearch, status: reduxStatus, sortColumn: reduxSortColumn, sortType: reduxSortType }),
    [route.search, reduxSearch, reduxStatus, reduxSortColumn, reduxSortType]
  );

  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>(
    params
  );
  // Sorting
  const refColumnId = useRef(queryParams.sortColumn ?? 'startTimestamp');
  const refSortType = useRef(queryParams.sortType ?? 'asc');
  const maintenanceNameRef = useRef(false);
  const impactedComponentsRef = useRef(false);
  const startDateRef = useRef(false);
  const endDateRef = useRef(false);
  const currentSortedColumn = useRef('');
  const [loader, setLoader] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectMainName, setSelectMainName] = useState('');
  // Search
  const [searchValue, setSearchValue] = useState(queryParams.search ?? '');
  // Pagination
  const [page, setPage] = useState(0);
  const [item, setItem] = useState(params.item ? Number(params.item) : getPreferredPageSize());
  const [status, setStatus] = useState<string>(queryParams.status ?? '');
  const [selectedSortField, setSelectedSortField] = useState(
    queryParams.sortColumn ?? 'startTimestamp'
  );
  const [selectedSortFieldOrder, setSelectedSortFieldOrder] = useState(
    (queryParams.sortType ? queryParams.sortType === 'ASC' : true)
  );
  const sortedObj = {
    sortedName: selectedSortField,
    sortedOrder: selectedSortFieldOrder ? 'ASC' : 'DESC'
  };
  const [applyEnabled, setApplyEnabled] = useState(false);

  const [selectedState, setSelectedState] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IServiceScheduleMaintenance | undefined>();
  const [isDataLoading, setISDataLoading] = useState(true);
  /**
   * To get path for respective tab number
   * @param number
   * @returns the selected page
   */
  const onGetPageNumber = (page: number) => {
    setPage(page);
    setQueryParams((pre) => ({
      ...pre,
      page: String(page),
    }));
  };
  /**
   * To get path for respective tab number
   * @param number
   * @returns total item in a page
   */
  const onGetItemsPerPage = (item: number) => {
    setItem(item);
    setQueryParams((pre) => ({
      ...pre,
      item: String(item),
    }));
  };

  /**
   * To get path for respective tab number
   * @returns default page and item (count)
   */
  const setPageDefault = () => {
    setPage(0);
    setItem(getPreferredPageSize());
  };

  const paginationParamUtil = (setPage: Dispatch<SetStateAction<number>>, setItem: Dispatch<SetStateAction<number>>, queryParams: { [key: string]: string }, defaultPageSize: number) => {
    setPage(queryParams.page === undefined || queryParams.page === '' ? 0 : Number(queryParams.page));
    setItem(queryParams.item === undefined || queryParams.item === '' ? defaultPageSize : Number(queryParams.item));
  };

  const { success: successToast, failure, renderToast } = useToastMessage();

  const { DeleteScheduledMaintenanceDetails, success: deleteSuccess, errorDeleteMessage } = useSelector((state) => state?.DeleteScheduledMaintenanceReducer);

  const { MaintenanceStatusDetails, savesuccess, errorStatusMessage } = useSelector((state) => state?.PostMaintenanceStatusReducer);

  /**
   * To get path for respective tab number
   * @returns data with applied filter
   */
  const handleMultiCondition = () => {
    if (queryParams.sortColumn === 'maintenanceName') {
      maintenanceNameRef.current = true;
      currentSortedColumn.current = translate('sm_table_wrapper.txt_maintenance_name');
    } else if (queryParams.sortColumn === 'impactedServices') {
      impactedComponentsRef.current = true;
      currentSortedColumn.current = translate('sm_table_wrapper.txt_impacted_components');
    } else if (queryParams.sortColumn === 'startTimestamp') {
      startDateRef.current = true;
      currentSortedColumn.current = translate('sm_table_wrapper.txt_start_date');
    } else if (queryParams.sortColumn === 'endTimestamp') {
      endDateRef.current = true;
      currentSortedColumn.current = translate('sm_table_wrapper.txt_end_date');
    } else {
      startDateRef.current = true;
      currentSortedColumn.current = '';
    }
  };

  /**
   * To get path for respective tab number
   * @returns get filter condition from query param and set it to table
   */
  const setFilterStates = () => {
    setStatus(queryParams.status ?? '');
    setSearchValue(queryParams.search ?? '');
    if (queryParams.sortColumn && queryParams.sortType) {
      refColumnId.current = queryParams.sortColumn;
      refSortType.current = queryParams.sortType;
      handleMultiCondition();
    }
    paginationParamUtil(setPage, setItem, queryParams, getPreferredPageSize());

    // Sync filters to Redux
    dispatch(addFilterMaintenanceData({
      searchText: queryParams.search ?? '',
      status: queryParams.status ?? ''
    }));
    dispatch(addSortMaintenanceData({
      sortColumn: queryParams.sortColumn ?? 'startTimestamp',
      sortType: queryParams.sortType ?? 'ASC'
    }));
  };
  const { ScheduledMaintenanceDetails, isLoading, errorMessage } = useSelector(
    (state: any) => state?.ScheduledMaintenanceReducer
  );

  const footerObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: ScheduledMaintenanceDetails?.metadata?.totalNumberOfPages,
    onCurrentPageNumberChange: onGetPageNumber,
    onItemsPerPageChange: onGetItemsPerPage
  };

  /**
   * To get path for respective tab number
   * @returns get filter condition either "ASC -> ascending" or "DESC -> descending"
   */
  const sortHandler = (label: string, selectedLabelState: boolean) => {
    setQueryParams((pre) => ({
      ...pre,
      sortColumn: label,
      sortType: selectedLabelState ? 'ASC' : 'DESC'
    }));
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (selectedSortField === label) {
      setSelectedSortFieldOrder((preValue) => {
        sortHandler(selectedSortField, !preValue);
        return !preValue;
      });
    } else {
      setSelectedSortField(label);
      setSelectedSortFieldOrder(true);
      sortHandler(label, true);
    }
  };

  /**
   * @method payloadDispatch
   * @description returns the list of data to show in maintenance table
   * @param search
   * @param status
   * @param columnId
   * @param sortType
   * @returns dataArr
   */
  const payloadDispatch = (search: string, status: string, columnId: string, sortType: string) => {
    const payload: ScheduledMaintenanceRqst = {
      maintenanceName: '',
      maintenanceStatus: '',
      pageNumber: page,
      pageSize: item,
      columnId: columnId !== '' ? columnId : 'startTimestamp',
      sortType: sortType !== '' ? sortType : 'ASC'
    };
    if (search !== '') {
      payload.maintenanceName = search;
    }
    if (status !== '') {
      payload.maintenanceStatus = status;
    }
    dispatch(getScheduledMaintenanceRequest(payload));
  };

  /**
   * @method handleClearAction
   * @description clear all filter applied on table
   */
  const handleClearAction = () => {
    setApplyEnabled(false);
    setPage(0);
    setQueryParams((pre) => ({
      ...pre,
      status: '',
      page: '0'
    }));
    setStatus('');
    payloadDispatch(searchValue, '', sortedObj.sortedName, sortedObj.sortedOrder);
  };

  const appliedFilterChangeHandler = () => {
    // The only possible change from the applied filters is the removal of the single status filter,
    // which is logically the same as a clear action.
    // If additional filters are added, this function will need to be updated.
    handleClearAction();
  };

  /**
   * @method handleCheckboxChange
   * @description set the selected option in dropdown and then dispatch the request
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setApplyEnabled(true);
    setStatus(e.target.value);
    setQueryParams((pre) => ({
      ...pre,
      search: searchValue,
      status: e.target.value ?? ''
    }));
  };

  /**
   * @method handleInnerCheckboxChange
   * @param name
   * @param item
   * @description set the selected option in dropdown and then dispatch the request
   */
  const handleInnerCheckboxChange = (name: string, item: IServiceScheduleMaintenance,) => {
    const statusUpdatePayload = {
      maintenanceName: item.maintenanceName,
      maintenanceStatus: name
    };
    setLoader(true);
    dispatch(getMaintenanceStatusRequest(statusUpdatePayload));
  };

  /**
   * @method appliedFilterData
   * @description push the selected dropdown value into data array and vice-versa
   * @returns data
   */
  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    if (!status) {
      return [];
    }
    if (status) {
      data.push({ text: status, type: status, value: status });
    }
    return data;
  }, [status]);

  const showResetFiltersButton = useMemo(() => !!status, [status]);

  /**
   * @method handleDelete
   * @description delete the selected maintenance if user select delete maintenance and then confirm delete else don't delete.
   */
  const handleDelete = (name: string) => {
    setSelectMainName(name);
    setShowDeleteModal(true);
  };
  const confirmDelete = () => {
    setShowDeleteModal(false);
    const deletePayload = {
      maintenanceName: selectMainName
    };
    deleteDispatch(deleteScheduledMaintenanceRequest(deletePayload));
  };
  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const confirmUpdate = () => {
    setIsConfirmed(true);
    setShowUpdateModal(false);
  };
  const cancelUpdate = () => {
    setIsConfirmed(false);
    setShowUpdateModal(false);
  };

  const renderMaintenanceStatus = (item: IServiceScheduleMaintenance) => (
    <div className={classes['maintenance-status']}>
      <InnerDrop
        item={item}
        handleInnerCheckboxChange={handleInnerCheckboxChange}
        setSelectedState={setSelectedState}
        setShowUpdateModal={setShowUpdateModal}
        setSelectedItem={setSelectedItem}
      />
    </div>
  );

  useEffect(() => {
    if (isLoading === false) setISDataLoading(false);
  }, [isLoading]);

  // Query Params
  useEffect(() => {
    if (Object.keys(queryParams).length !== 0) {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v) {
          searchParams.set(k, v);
        }
      });

      setFilterStates();
      const queryString = searchParams.toString();
      navigate(
        queryString ? `?${queryString}` : route.search,
        { state: route.search }
      );
    }
  }, [queryParams]);

  useEffect(() => {
    payloadDispatch(searchValue, status, sortedObj.sortedName, sortedObj.sortedOrder);
  }, [dispatch, item, page, searchValue, deleteSuccess, sortedObj.sortedName, sortedObj.sortedOrder]);

  useEffect(() => {
    if (selectedState === 'COMPLETED') {
      if (isConfirmed && selectedItem) {
        handleInnerCheckboxChange(selectedState, selectedItem);
        setIsConfirmed(false);
      }
    }
  }, [selectedState, isConfirmed, savesuccess, errorStatusMessage]);

  useEffect(() => {
    payloadDispatch(searchValue, status, sortedObj.sortedName, sortedObj.sortedOrder);
  }, [savesuccess, errorStatusMessage]);

  useEffect(() => {
    setLoader(false);
    if (savesuccess) {
      successToast(translate('sm_table_wrapper.txt_success'), translate('sm_table_wrapper.txt_status_updated'));
      dispatch(cleargetMaintenanceStatus());
    }
    if (errorStatusMessage) {
      failure(translate('sm_table_wrapper.txt_error'), translate('sm_table_wrapper.txt_error_desc'));
      dispatch(cleargetMaintenanceStatus());
    }
    if (deleteSuccess) {
      successToast(translate('sm_table_wrapper.txt_success'), translate('sm_table_wrapper.txt_maintenance_deleted'));
      dispatch(cleardeleteScheduledMaintenance());
    }
    if (errorDeleteMessage !== '') {
      failure(translate('sm_table_wrapper.txt_error'), translate('sm_table_wrapper.txt_unable_to_delete'));
      dispatch(cleardeleteScheduledMaintenance());
    }
  }, [MaintenanceStatusDetails, errorMessage, errorStatusMessage, DeleteScheduledMaintenanceDetails, errorDeleteMessage]);

  useEffect(() => {
    if (queryParams.item) {
      persistPageSize(Number(queryParams.item));
    }
  }, [queryParams.item]);

  const addBtnHandler = () => {
    navigate(`${SCHEDULE_MAINTENANCE_ROUTE.CREATE_SM_ROUTE}`, {});
    dispatch(clearSmLogHistory());
    dispatch(clearSaveMaintenance());
  };

  return (
    <>
      {renderToast()}
      <Modal
        modalTitle={translate('sm_table_wrapper.txt_confirm_delete')}
        modalBody={translate('sm_table_wrapper.txt_confirm_delete_desc')}
        modalShow={showDeleteModal}
        btnSaveText={translate('sm_table_wrapper.txt_delete') ?? ''}
        btnCloseText={translate('sm_table_wrapper.txt_not_delete') ?? ''}
        onSaveModal={confirmDelete}
        onCloseModal={cancelDelete}
        alignToCentre
        disableSaveBtn={false}
        disableCloseBtn={false}
        type="warning"
      />
      <div className={classes['role-container']} data-testid="role-container">
        <div className={classes.filters}>
          <HeadFilters
            setPageDefault={setPageDefault}
            setPage={setPage}
            setQueryParams={setQueryParams}
            status={status}
            payloadDispatch={payloadDispatch}
            searchValue={searchValue}
            handleCheckboxChange={handleCheckboxChange}
            handleClearAction={handleClearAction}
            sortedObj={sortedObj}
            applyEnabled={applyEnabled}
            setApplyEnabled={setApplyEnabled}
            addBtnHandler={addBtnHandler}
          />
          <CIAppliedListFilter
            className={classes['applied-filters']}
            totalRecords={ScheduledMaintenanceDetails?.metadata?.totalNumberOfElements}
            appliedFilters={appliedFilterData}
            onFilterChange={appliedFilterChangeHandler}
            onResetFilters={handleClearAction}
            showResetFiltersButton={showResetFiltersButton}
          />

        </div>
        {errorMessage && <UnableToFetch />}
        {!errorMessage && (
          <>
            {ScheduledMaintenanceDetails?.serviceScheduleMaintenance?.length > 0 && (
              <div className={classes['table-grid-wrapper']} data-testid="table-data">
                <div className="mt-2">
                  {(isLoading || loader) && <Loader show />}
                  <div className={classes['dynamic-card-body']}>
                    <MaintenanceTableList
                      handleClick={handleClick}
                      renderMaintenanceStatus={renderMaintenanceStatus}
                      handleDelete={handleDelete}
                      sortedObj={sortedObj}
                      footerObj={footerObj}
                    />
                  </div>
                </div>
              </div>
            )}
            {ScheduledMaintenanceDetails?.serviceScheduleMaintenance?.length === 0 && !isDataLoading && (
              <NoDataAvailable />)}
          </>
        )}
      </div>
      {selectedState && selectedState === 'COMPLETED' && (
        <Modal
          modalTitle={translate('sm_table_wrapper.txt_confirm_update')}
          modalBody={translate('sm_table_wrapper.txt_update_desc')}
          modalShow={showUpdateModal}
          btnSaveText={translate('sm_table_wrapper.txt_update') ?? ''}
          btnCloseText={translate('sm_table_wrapper.txt_not_update') ?? ''}
          onSaveModal={confirmUpdate}
          onCloseModal={cancelUpdate}
          alignToCentre
          disableSaveBtn={false}
          disableCloseBtn={false}
          type="warning"
        />)}
    </>
  );
};
